import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Segmented } from '@/components/ui/Segmented'
import { Textarea } from '@/components/ui/Textarea'

import { Dropzone } from '@/components/shared'

/* The columns of the real cards table, not the fixture's. cr_name is the unique
 * key the server matches on; the rest are optional and fall back to the column
 * defaults. */
const TEMPLATE = 'cr_name,name,rarity,card_type,cost,arena,count'
const RARITIES = ['common', 'rare', 'epic']
const CARD_TYPES = ['spell', 'troop', 'building']

type ImportMode = 'append' | 'replace'

export interface ImportedCard {
  cr_name: string
  name: string
  rarity: string
  card_type: string
  cost: number
  arena: number
  count: number
}

/** Returns the rows it could parse and a count for the ones it could not,
 *  rather than throwing — a half-good paste should still be usable. */
export function parseCardCsv(text: string): { cards: ImportedCard[]; skipped: number } {
  const cards: ImportedCard[] = []
  let skipped = 0

  for (const line of text.split('\n')) {
    const row = line.trim()
    if (!row || row.toLowerCase().startsWith('cr_name,')) continue

    const [cr_name, name, rarity, card_type, cost, arena, count] = row
      .split(',')
      .map((cell) => cell.trim().replace(/^"|"$/g, ''))

    /* cr_name, rarity and card_type are validated server-side too; rejecting
       them here just means the bad rows never leave the browser. */
    if (!cr_name || !RARITIES.includes(rarity) || !CARD_TYPES.includes(card_type)) {
      skipped += 1
      continue
    }

    cards.push({
      cr_name,
      name: name || cr_name,
      rarity,
      card_type,
      cost: Number(cost) || 0,
      arena: Number(arena) || 0,
      count: Number(count) || 1,
    })
  }

  return { cards, skipped }
}

interface ImportCardsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onImport: (cards: ImportedCard[], mode: ImportMode) => void
}

export function ImportCardsDialog({ open, onOpenChange, onImport }: ImportCardsDialogProps) {
  const [text, setText] = useState('')
  const [fileName, setFileName] = useState<string>()
  const [mode, setMode] = useState<ImportMode>('append')

  const { cards, skipped } = parseCardCsv(text)

  const reset = () => {
    setText('')
    setFileName(undefined)
    setMode('append')
  }

  const handleFile = (file: File) => {
    setFileName(file.name)
    void file.text().then(setText)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
      className="w-[520px]"
    >
      <DialogHeader>
        <DialogTitle>Import cards</DialogTitle>
        <DialogDescription>
          Expected columns: <span className="text-fg">{TEMPLATE}</span>
        </DialogDescription>
      </DialogHeader>

      <DialogBody className="flex flex-col gap-3">
        <Dropzone
          accept=".csv,text/csv"
          fileName={fileName}
          onFile={handleFile}
          hint="Choose a .csv file, or paste below"
        />

        <Field label="Paste CSV" htmlFor="import-csv">
          <Textarea
            id="import-csv"
            rows={6}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={`${TEMPLATE}\nEmber Imp,Common,3,120,540`}
            className="text-sm"
          />
        </Field>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-fg-muted text-sm">
            {cards.length === 0
              ? 'Nothing to import yet.'
              : `${cards.length} row${cards.length === 1 ? '' : 's'} ready`}
            {skipped > 0 && <span className="text-warn"> · {skipped} skipped</span>}
          </span>
          <Segmented
            aria-label="Import mode"
            options={[
              { value: 'append', label: 'Append' },
              { value: 'replace', label: 'Replace all' },
            ]}
            value={mode}
            onValueChange={setMode}
          />
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          disabled={cards.length === 0}
          onClick={() => {
            onImport(cards, mode)
            onOpenChange(false)
            reset()
          }}
        >
          Import Rows
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
